/**
 * Results v2 state matrix (docs/design/v3.5-results-system-v2.md §2).
 *
 * Outcome × evidence basis has 18 cells; 9 are valid and 9 are impossible by
 * construction of the evaluator, not by convention. This module is the single
 * place that knowledge lives on the client: components render FROM a resolved
 * state, never from ad-hoc outcome checks, so an impossible combination
 * cannot be styled into something plausible.
 *
 * An impossible cell throws in dev — the defect surfaces at its source — and
 * in production resolves to the safe open treatment: the outcome label still
 * states what the server said, but nothing claims an evidence basis the
 * matrix says cannot exist.
 *
 * ARC IP boundary: outcome identifiers and structure only. Nothing here
 * carries catalog content.
 */

import type { FindingOutcome, RunFinding } from './assessmentTypes';

export type AnsweredOutcome = 'satisfied' | 'marginal' | 'not_satisfied';
export type AnsweredTone = 'good' | 'soft' | 'hard';
export type OpenTreatment = 'attestation_required' | 'not_assessable' | 'collector_error';

export type FindingVisualState =
  | {
      kind: 'answered';
      outcome: AnsweredOutcome;
      /** Texture axis: solid fill = measured, 45° hatch = attested (§3.2). */
      basis: 'measured' | 'attested';
      tone: AnsweredTone;
      /** band/score exist only inside the answered region (§2, consequence 1). */
      banded: boolean;
    }
  | {
      kind: 'open';
      /** Which of the three §3.3 treatments to render — never one grey. */
      treatment: OpenTreatment;
      outcome: FindingOutcome;
      /** true only when the matrix rejected the stored combination in prod. */
      degraded: boolean;
    };

/** The 9 valid cells of the 18-cell matrix (§2). */
const VALID_CELLS: Record<FindingOutcome, readonly RunFinding['confidence'][]> = {
  satisfied: ['measured', 'attested'],
  marginal: ['measured', 'attested'],
  not_satisfied: ['measured', 'attested'],
  attestation_required: ['absent'],
  not_assessable: ['absent'],
  collector_error: ['absent'],
};

const TONE: Record<AnsweredOutcome, AnsweredTone> = {
  satisfied: 'good',
  marginal: 'soft',
  not_satisfied: 'hard',
};

function isAnsweredOutcome(outcome: FindingOutcome): outcome is AnsweredOutcome {
  return outcome === 'satisfied' || outcome === 'marginal' || outcome === 'not_satisfied';
}

export type MatrixFields = Pick<RunFinding, 'testCode' | 'outcome' | 'confidence' | 'band'>;

/**
 * Resolve a finding to the one visual state the matrix allows it.
 *
 * Structural rules enforced beyond the cell itself:
 *  - `band` exists only in the answered region — a banded unanswered finding
 *    is a contradiction in terms;
 *  - `marginal` is always banded: a band requires a value (§2).
 */
export function resolveFindingState(finding: MatrixFields): FindingVisualState {
  const { outcome, confidence, band } = finding;

  const cellValid = (VALID_CELLS[outcome] ?? []).includes(confidence);
  const bandValid =
    (band === null || isAnsweredOutcome(outcome)) && !(outcome === 'marginal' && band === null);

  if (!cellValid || !bandValid) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(
        `Impossible finding state on ${finding.testCode}: ` +
          `${outcome} × ${confidence}${band !== null ? ' (banded)' : ''} ` +
          'is not a valid cell of the Results v2 state matrix (spec §2).'
      );
    }
    // Prod: the neutral open treatment — no fill claims answered-ness, the
    // dashed neutral border claims no outage and no pending person.
    return { kind: 'open', treatment: 'not_assessable', outcome, degraded: true };
  }

  if (isAnsweredOutcome(outcome)) {
    return {
      kind: 'answered',
      outcome,
      basis: confidence as 'measured' | 'attested',
      tone: TONE[outcome],
      banded: band !== null,
    };
  }

  return { kind: 'open', treatment: outcome as OpenTreatment, outcome, degraded: false };
}
